import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, ChevronDown, ChevronRight,
  Circle, Clock, Copy, Lock, Plus, Send, Trash2, FileText, ShieldCheck, Download,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../lib/auth.jsx';
import { formatDateTime } from '../lib/constants';

const RISK_TONE = {
  bajo:    { color: '#065F46', bg: '#ECFDF5', border: '#A7F3D0', label: 'Bajo' },
  medio:   { color: '#B45309', bg: '#FFFBEB', border: '#FDE68A', label: 'Medio' },
  alto:    { color: '#B91C1C', bg: '#FEF2F2', border: '#FCA5A5', label: 'Alto' },
  critico: { color: '#7F1D1D', bg: '#FEE2E2', border: '#F87171', label: 'Crítico' },
};

const TASK_STATUS = {
  pendiente:  { label: 'Pendiente',  color: '#6B7280', bg: '#F3F4F6' },
  en_curso:   { label: 'En curso',   color: '#1E40AF', bg: '#EFF6FF' },
  completada: { label: 'Completada', color: '#065F46', bg: '#ECFDF5' },
  no_aplica:  { label: 'No aplica',  color: '#6B7280', bg: '#F9FAFB' },
};


export default function BrechaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canExport = user && ['admin', 'compliance_officer'].includes(user.role);
  const [b, setB] = useState(null);
  const [meta, setMeta] = useState(null);
  const [team, setTeam] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [currentStage, setCurrentStage] = useState('contencion');
  const [stagesCompleted, setStagesCompleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTemplates, setShowTemplates] = useState(false);
  const [agenciaText, setAgenciaText] = useState('');
  const [titularesText, setTitularesText] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [breach, m, t, tks] = await Promise.all([
        api.get(`/breaches/${id}`),
        api.get('/breaches/meta'),
        api.get('/team/members'),
        api.get(`/breaches/${id}/tasks`),
      ]);
      setB(breach.data);
      setMeta(m.data);
      setTeam((t.data || []).filter((u) => u.is_active !== false));
      setTasks(tks.data.tasks);
      setCurrentStage(tks.data.current_stage);
      setStagesCompleted(tks.data.stages_completed || []);
      setAgenciaText(breach.data.notification_agencia_text || '');
      setTitularesText(breach.data.notification_titulares_text || '');
    } catch (e) {
      toast.error('No se pudo cargar la brecha');
      navigate('/app/brechas');
    } finally { setLoading(false); }
  }, [id, navigate]);

  const refreshTasks = useCallback(async () => {
    const { data } = await api.get(`/breaches/${id}/tasks`);
    setTasks(data.tasks);
    setCurrentStage(data.current_stage);
    setStagesCompleted(data.stages_completed || []);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const stageTasks = useMemo(
    () => tasks.filter((t) => t.stage === currentStage),
    [tasks, currentStage],
  );
  const stageLegalPending = stageTasks.filter(
    (t) => t.legal_obligatoria && t.status !== 'completada',
  ).length;

  if (loading || !b || !meta) {
    return (
      <div className="p-6">
        <button onClick={() => navigate('/app/brechas')} className="text-[13px] text-[#6B7280] hover:text-[#143A34] inline-flex items-center gap-1 mb-6">
          <ArrowLeft size={14} /> Volver a brechas
        </button>
        <div className="text-sm text-[#6B7280]">Cargando…</div>
      </div>
    );
  }

  const stages = meta.stages || [];
  const risk = RISK_TONE[b.risk_level] || RISK_TONE.medio;
  const hoursLeft = Math.ceil((new Date(b.deadline_agencia) - new Date()) / (1000 * 3600));
  const notified = !!b.notification_agencia_sent_at;
  const overdue = hoursLeft < 0 && !notified;
  const isLastStage = currentStage === stages[stages.length - 1]?.key;
  const isStageClosed = stagesCompleted.includes(currentStage);
  const legalTasksTotal = tasks.filter((t) => t.legal_obligatoria).length;
  const legalTasksDone = tasks.filter((t) => t.legal_obligatoria && t.status === 'completada').length;
  const currentStageInfo = stages.find((s) => s.key === currentStage);

  const patchBreach = async (payload) => {
    try {
      const { data } = await api.patch(`/breaches/${b.id}`, payload);
      setB(data); toast.success('Actualizado');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const exportBreach = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await api.post(`/breaches/${b.id}/export`, null, { responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const m = /filename="?([^"]+)"?/.exec(cd);
      const filename = m ? m[1] : `Priviax_Brecha_${b.id.slice(0, 8)}.zip`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      const hash = res.headers['x-priviax-sha256'];
      toast.success(hash ? `Carpeta descargada · SHA-256 ${hash.slice(0, 12)}…` : 'Carpeta descargada');
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error('Solo Administradores o Compliance Officers pueden descargar esta carpeta.');
      } else {
        const detail = e?.response?.data?.detail;
        toast.error(typeof detail === 'string' ? detail : 'No se pudo generar la carpeta');
      }
    } finally { setExporting(false); }
  };

  const advance = async () => {
    try {
      const { data } = await api.post(`/breaches/${b.id}/advance-stage`);
      setB(data);
      setStagesCompleted(data.stages_completed || []);
      setCurrentStage(data.current_stage);
      toast.success(data.status === 'cerrada' ? 'Brecha cerrada' : 'Etapa avanzada');
      await refreshTasks();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.blockers?.length) {
        toast.error(`Complete primero ${detail.blockers.length} tarea(s) legal(es) pendiente(s)`);
      } else { toast.error(detail?.message || detail || 'No se puede avanzar'); }
    }
  };

  return (
    <div className="-mx-6 lg:-mx-10 -my-8 min-h-[calc(100vh-0px)] flex flex-col" data-testid="brecha-detail">
      {/* ============ ENCABEZADO FIJO ============ */}
      <div className="sticky top-0 z-30 bg-[#F7F8FA] border-b border-[#E5E7EB] shadow-sm">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-5 pb-4 space-y-4">

          {/* Back + título compacto */}
          <div>
            <div className="flex items-center justify-between gap-2">
              <button onClick={() => navigate('/app/brechas')} className="text-[12px] text-[#6B7280] hover:text-[#143A34] inline-flex items-center gap-1"
                data-testid="back-to-list">
                <ArrowLeft size={13} /> Volver a brechas
              </button>
              <button
                onClick={exportBreach}
                disabled={exporting || !canExport}
                className="text-[11.5px] px-2.5 py-1 border border-[#143A34] text-[#143A34] hover:bg-[#143A34] hover:text-white inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition"
                title={canExport
                  ? 'Descarga un ZIP hash-firmado (SHA-256) con toda la evidencia de este incidente.'
                  : 'Solo Administradores o Compliance Officers pueden descargar esta carpeta.'}
                data-testid="export-breach-btn">
                <Download size={12} /> {exporting ? 'Generando…' : 'Descargar carpeta de esta brecha'}
              </button>
            </div>
            <div className="flex items-baseline gap-3 mt-1 flex-wrap">
              <h1 className="text-[20px] font-semibold text-[#1C2329] tracking-[-0.02em] leading-tight">{b.title}</h1>
              <div className="text-[11px] text-[#6B7280] font-mono">Ref {b.id.slice(0, 8)} · {meta.types[b.incident_type]}</div>
            </div>
          </div>

          {/* Stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            <StatCard label="Nivel de riesgo" value={risk.label.toUpperCase()}
              sub={b.affected_data_categories?.length ? `${b.affected_data_categories.length} cat. de datos` : 'Sin datos aún'}
              color={risk.color} bg={risk.bg} border={risk.border} />
            <StatCard label="Notif. Agencia (72h)"
              value={notified ? 'ENVIADA' : overdue ? `${Math.abs(hoursLeft)}H VENCIDA` : `${hoursLeft}H`}
              sub={notified ? formatDateTime(b.notification_agencia_sent_at) : `Vence ${formatDateTime(b.deadline_agencia)}`}
              color={notified ? '#065F46' : overdue ? '#7F1D1D' : hoursLeft <= 12 ? '#B91C1C' : hoursLeft <= 24 ? '#B45309' : '#065F46'}
              bg={notified ? '#ECFDF5' : overdue ? '#FEE2E2' : hoursLeft <= 12 ? '#FEF2F2' : hoursLeft <= 24 ? '#FFFBEB' : '#ECFDF5'}
              border={notified ? '#A7F3D0' : overdue ? '#F87171' : hoursLeft <= 12 ? '#FCA5A5' : hoursLeft <= 24 ? '#FDE68A' : '#A7F3D0'}
              highlight />
            <StatCard label="Etapa actual"
              value={`${stages.findIndex((s) => s.key === b.current_stage) + 1} DE 6`}
              sub={stages.find((s) => s.key === b.current_stage)?.label || '—'}
              color="#0F2A26" bg="#F0F7F5" border="#B7D8CE" />
            <StatCard label="Tareas legales"
              value={`${legalTasksDone} / ${legalTasksTotal}`}
              sub={legalTasksDone === legalTasksTotal ? 'Al día' : `${legalTasksTotal - legalTasksDone} pendientes`}
              color={legalTasksDone === legalTasksTotal ? '#065F46' : '#B45309'}
              bg={legalTasksDone === legalTasksTotal ? '#ECFDF5' : '#FFFBEB'}
              border={legalTasksDone === legalTasksTotal ? '#A7F3D0' : '#FDE68A'} />
          </div>

          {/* Plantillas (colapsables, ahora debajo de las cajas) */}
          <div className="border border-[#D1D5DB] bg-white">
            <button onClick={() => setShowTemplates((s) => !s)}
              className="w-full px-3 py-2 flex items-center justify-between text-[12.5px] font-medium text-[#1C2329] hover:bg-[#FAFBFC]"
              data-testid="toggle-templates">
              <span className="flex items-center gap-2">
                <Send size={12} /> Plantillas de notificación
                {b.notification_agencia_sent_at && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-[#ECFDF5] text-[#065F46] font-medium">Agencia · enviada</span>
                )}
                {b.notification_titulares_sent_at && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-[#ECFDF5] text-[#065F46] font-medium">Titulares · enviada</span>
                )}
              </span>
              <ChevronDown size={13} className={`transition-transform ${showTemplates ? 'rotate-180' : ''}`} />
            </button>
            {showTemplates && (
              <div className="p-3 space-y-3 border-t border-[#D1D5DB]">
                <TemplateBlock title="Notificación a la Agencia de Protección de Datos"
                  text={agenciaText} onChange={setAgenciaText}
                  sent={b.notification_agencia_sent_at}
                  onMarkSent={() => patchBreach({ notification_agencia_text: agenciaText, mark_agencia_sent: true })}
                  testid="tpl-agencia" />
                <TemplateBlock title="Comunicación a los titulares afectados"
                  text={titularesText} onChange={setTitularesText}
                  sent={b.notification_titulares_sent_at}
                  onMarkSent={() => patchBreach({ notification_titulares_text: titularesText, mark_titulares_sent: true })}
                  testid="tpl-titulares" />
              </div>
            )}
          </div>

          {/* Stepper */}
          <div className="bg-white border border-[#E5E7EB] px-3 py-2 overflow-x-auto">
            <div className="flex items-center gap-1 min-w-max">
              {stages.map((s, i) => {
                const isDone = stagesCompleted.includes(s.key);
                const isCurrent = s.key === currentStage;
                return (
                  <div key={s.key} className="flex items-center">
                    <button onClick={() => setCurrentStage(s.key)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 border text-[11.5px] transition ${
                        isCurrent ? 'border-[#143A34] bg-[#143A34] text-white' :
                        isDone ? 'border-[#10B981] bg-[#ECFDF5] text-[#065F46]' :
                        'border-[#E5E7EB] bg-white text-[#6B7280] hover:border-[#9CA3AF]'
                      }`} data-testid={`stage-${s.key}`}>
                      {isDone ? <CheckCircle2 size={11} /> : <Circle size={11} strokeWidth={isCurrent ? 2 : 1.5} />}
                      <span className="font-medium whitespace-nowrap">{i + 1}. {s.label}</span>
                    </button>
                    {i < stages.length - 1 && <ChevronRight size={11} className="text-[#D1D5DB] mx-0.5 flex-shrink-0" />}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ============ CUERPO SCROLLEABLE ============ */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-5 space-y-5 pb-32">

          {/* Intro etapa */}
          {currentStageInfo && (
            <div className="border-l-4 border-[#143A34] bg-[#F0F7F5] px-4 py-2.5">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-[10.5px] uppercase tracking-wider text-[#065F46] font-medium">Etapa {currentStageInfo.order} de 6</span>
                <span className="text-[14px] font-semibold text-[#0F2A26]">{currentStageInfo.label}</span>
              </div>
              <div className="text-[12px] text-[#374151] mt-0.5 leading-relaxed">{currentStageInfo.goal}</div>
            </div>
          )}

          {/* Banner de etapa cerrada */}
          {isStageClosed && (
            <div className="border border-[#D1D5DB] bg-[#F3F4F6] px-4 py-3 flex items-start gap-2.5"
              data-testid="stage-locked-banner">
              <Lock size={14} className="text-[#6B7280] mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-[12.5px] font-medium text-[#374151]">Etapa cerrada · solo lectura</div>
                <div className="text-[11.5px] text-[#6B7280] mt-0.5">
                  Las tareas de esta etapa quedaron bloqueadas al avanzar. Si necesita corregir información, contacte al DPO.
                </div>
              </div>
            </div>
          )}

          {/* Lista de tareas */}
          <TaskList tasks={stageTasks} team={team} breachId={b.id}
            stageKey={currentStage} onChanged={refreshTasks}
            readOnly={isStageClosed} />
        </div>
      </div>

      {/* ============ BARRA DE AVANCE FIJA ============ */}
      <div className="sticky bottom-0 z-30 bg-white border-t border-[#E5E7EB] shadow-lg">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="text-[12px] text-[#6B7280]">
            {isStageClosed
              ? <span className="text-[#374151]"><CheckCircle2 size={12} className="inline mr-1 text-[#10B981]" /> Esta etapa ya fue cerrada.</span>
              : stageLegalPending > 0
              ? <span><Lock size={12} className="inline mr-1 text-[#B45309]" /> Faltan <b className="text-[#1C2329]">{stageLegalPending}</b> tarea{stageLegalPending === 1 ? '' : 's'} legal{stageLegalPending === 1 ? '' : 'es'} de esta etapa para poder avanzar.</span>
              : <span className="text-[#065F46]"><CheckCircle2 size={12} className="inline mr-1" /> Todas las tareas legales de esta etapa están completadas.</span>}
          </div>
          <button
            className="px-4 py-2 bg-[#143A34] hover:bg-[#0F2A26] text-white text-[13px] font-medium flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={isStageClosed || stageLegalPending > 0 || b.status === 'cerrada'}
            onClick={advance} data-testid="advance-stage">
            {isLastStage ? <>Cerrar brecha <CheckCircle2 size={13} /></> : <>Avanzar a siguiente etapa <ArrowRight size={13} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}


// ============ Stat card compacta ============
function StatCard({ label, value, sub, color, bg, border, highlight }) {
  return (
    <div className="border-2 px-3 py-2" style={{ backgroundColor: bg, borderColor: border }}>
      <div className="text-[9.5px] uppercase tracking-wider font-medium" style={{ color, opacity: 0.75 }}>{label}</div>
      <div className={`font-semibold leading-none tracking-tight ${highlight ? 'text-[22px]' : 'text-[18px]'}`} style={{ color }}>
        {value}
      </div>
      <div className="text-[10.5px] mt-0.5 truncate" style={{ color, opacity: 0.7 }}>{sub}</div>
    </div>
  );
}


// ============ Lista de tareas ============
function TaskList({ tasks, team, breachId, stageKey, onChanged, readOnly }) {
  const [adding, setAdding] = useState(false);
  const legal = tasks.filter((t) => t.legal_obligatoria);
  const suggested = tasks.filter((t) => !t.legal_obligatoria && t.source !== 'custom');
  const custom = tasks.filter((t) => t.source === 'custom');

  return (
    <div className="space-y-5">
      {legal.length > 0 && (
        <TaskGroup title="Obligatorias por ley" subtitle="No se pueden eliminar. Complételas para cerrar la etapa."
          icon={<Lock size={12} className="text-[#B45309]" />}
          tasks={legal} team={team} breachId={breachId} onChanged={onChanged} readOnly={readOnly} />
      )}
      {suggested.length > 0 && (
        <TaskGroup title="Sugeridas según el tipo de incidente" subtitle="Aplican a este tipo. Si no aplican a su caso, márquelas «No aplica»."
          tasks={suggested} team={team} breachId={breachId} onChanged={onChanged} readOnly={readOnly} />
      )}
      {custom.length > 0 && (
        <TaskGroup title="Tareas propias" subtitle="Agregadas por su equipo para este incidente."
          tasks={custom} team={team} breachId={breachId} onChanged={onChanged}
          canDelete={!readOnly} readOnly={readOnly} />
      )}
      {!readOnly && (adding ? (
        <NewTaskForm stageKey={stageKey} team={team} breachId={breachId}
          onSaved={() => { setAdding(false); onChanged(); }}
          onCancel={() => setAdding(false)} />
      ) : (
        <button onClick={() => setAdding(true)}
          className="w-full border border-dashed border-[#D1D5DB] py-2.5 text-[12.5px] text-[#374151] hover:border-[#143A34] hover:text-[#143A34] hover:bg-[#F0F7F5] flex items-center justify-center gap-2"
          data-testid="add-task">
          <Plus size={13} /> Agregar tarea propia a esta etapa
        </button>
      ))}
    </div>
  );
}

function TaskGroup({ title, subtitle, icon, tasks, team, breachId, onChanged, canDelete, readOnly }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        {icon}
        <div>
          <div className="text-[12px] font-semibold text-[#1C2329]">{title}</div>
          <div className="text-[10.5px] text-[#6B7280]">{subtitle}</div>
        </div>
      </div>
      <div className="border border-[#D1D5DB] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        {tasks.map((t, i) => (
          <TaskRow key={t.id} task={t} team={team} breachId={breachId}
            onChanged={onChanged} canDelete={canDelete}
            isFirst={i === 0} rowIndex={i} readOnly={readOnly} />
        ))}
      </div>
    </div>
  );
}


// ============ FILA DE TAREA con confirmación de evidencia ============
function TaskRow({ task, team, breachId, onChanged, canDelete, isFirst, rowIndex, readOnly }) {
  const [descOpen, setDescOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [notesDraft, setNotesDraft] = useState(task.completion_notes || '');
  const status = TASK_STATUS[task.status] || TASK_STATUS.pendiente;
  const overdue = task.due_date && new Date(task.due_date) < new Date()
    && task.status !== 'completada' && task.status !== 'no_aplica';
  const isDone = task.status === 'completada';
  const isNA = task.status === 'no_aplica';
  const hasDesc = !!task.description;

  let bgClass = rowIndex % 2 === 1 ? 'bg-[#FAFBFC]' : 'bg-white';
  if (isDone) bgClass = 'bg-[#F0FDF4]';
  else if (isNA) bgClass = 'bg-[#F5F5F6]';
  else if (overdue) bgClass = 'bg-[#FEF2F2]';
  if (readOnly) bgClass += ' opacity-70';

  const patch = async (payload) => {
    try {
      await api.patch(`/breaches/${breachId}/tasks/${task.id}`, payload);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const startComplete = () => {
    if (readOnly) return;
    if (isDone) { patch({ status: 'pendiente' }); return; }
    if (!task.assignee_id) { toast.error('Asigne un responsable antes de completar'); return; }
    // Abrir formulario de evidencia
    setNotesDraft(task.completion_notes || '');
    setConfirming(true);
  };

  const confirmComplete = async () => {
    const minLen = task.legal_obligatoria ? 10 : 0;
    if (notesDraft.trim().length < minLen) {
      toast.error(`Debe describir en al menos ${minLen} caracteres qué se hizo`);
      return;
    }
    await patch({ status: 'completada', completion_notes: notesDraft.trim() });
    setConfirming(false);
  };

  const remove = async () => {
    if (!window.confirm(`¿Eliminar la tarea «${task.title}»?`)) return;
    try {
      await api.delete(`/breaches/${breachId}/tasks/${task.id}`);
      toast.success('Tarea eliminada'); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const overdueHours = overdue ? Math.abs(Math.ceil((new Date() - new Date(task.due_date)) / 3600000)) : 0;
  const disabled = readOnly;

  return (
    <div className={`${bgClass} ${!isFirst ? 'border-t border-[#D1D5DB]' : ''} px-4 py-2`} data-testid={`task-${task.id}`}>
      {/* LÍNEA 1 · título + meta inline */}
      <div className="flex items-center gap-2.5">
        <button onClick={startComplete} disabled={isNA || readOnly}
          className="flex-shrink-0 disabled:cursor-not-allowed"
          title={isDone ? 'Marcar como pendiente' : 'Marcar como completada'}
          data-testid={`task-toggle-${task.id}`}>
          {isDone
            ? <CheckCircle2 size={17} className="text-[#10B981]" />
            : <Circle size={17} className={readOnly ? 'text-[#D1D5DB]' : 'text-[#D1D5DB] hover:text-[#143A34]'} />}
        </button>

        <div className={`flex-1 min-w-0 text-[12.5px] font-medium truncate ${isDone ? 'text-[#065F46] line-through' : isNA ? 'text-[#9CA3AF] line-through' : 'text-[#1C2329]'}`}>
          {task.title}
        </div>

        {hasDesc && (
          <button onClick={() => setDescOpen((v) => !v)}
            className="text-[10.5px] text-[#6B7280] hover:text-[#143A34] inline-flex items-center gap-0.5 flex-shrink-0"
            data-testid={`task-desc-toggle-${task.id}`}>
            {descOpen ? 'Ocultar' : 'Ver descripción'}
            <ChevronDown size={9} className={`transition ${descOpen ? 'rotate-180' : ''}`} />
          </button>
        )}

        {overdue && (
          <span className="text-[10.5px] text-[#DC2626] font-medium inline-flex items-center gap-1 flex-shrink-0">
            <Clock size={10} /> Vencida hace {overdueHours}h
          </span>
        )}

        {task.legal_obligatoria && (
          <span className="text-[9.5px] px-1.5 py-0.5 bg-[#FEF3C7] text-[#92400E] font-medium inline-flex items-center gap-1 flex-shrink-0">
            <Lock size={8} /> Legal
          </span>
        )}
      </div>

      {/* Descripción expandida */}
      {descOpen && hasDesc && (
        <div className="ml-[27px] mt-1.5 text-[11.5px] text-[#374151] leading-relaxed bg-white border-l-2 border-[#E5E7EB] pl-2.5 py-1.5">
          {task.description}
          {task.legal_ref && (
            <div className="text-[10px] text-[#B45309] italic mt-1">Ref. legal: {task.legal_ref}</div>
          )}
          <div className="text-[10px] text-[#6B7280] mt-1.5 pt-1.5 border-t border-[#F3F4F6]">
            <b>¿Qué significa completar esta tarea?</b> Realizar la acción descrita y luego marcar la casilla dejando por escrito qué se hizo (queda como evidencia en la bitácora ante una fiscalización).
          </div>
        </div>
      )}

      {/* LÍNEA 2 · controles inline */}
      <div className="ml-[27px] mt-1.5 grid grid-cols-12 gap-1.5">
        <select
          className={`col-span-12 md:col-span-5 px-2 py-1 border text-[11.5px] bg-white ${!task.assignee_id ? 'border-[#FCA5A5]' : 'border-[#D1D5DB]'}`}
          value={task.assignee_id || ''} onChange={(e) => patch({ assignee_id: e.target.value })}
          disabled={disabled}
          data-testid={`task-assignee-${task.id}`} title="Responsable">
          <option value="">👤 Sin asignar</option>
          {team.map((u) => (
            <option key={u.id} value={u.id}>👤 {u.nombre} {u.apellido} · {u.email}</option>
          ))}
        </select>
        <input type="datetime-local"
          className="col-span-7 md:col-span-4 px-2 py-1 border border-[#D1D5DB] text-[11.5px] bg-white"
          value={task.due_date ? task.due_date.slice(0, 16) : ''}
          onChange={(e) => patch({ due_date: e.target.value ? e.target.value + ':00Z' : '' })}
          disabled={disabled}
          title="Fecha límite" />
        <select
          className="col-span-5 md:col-span-3 px-2 py-1 border border-[#D1D5DB] text-[11.5px] font-medium"
          style={{ color: status.color, backgroundColor: status.bg }}
          value={task.status}
          onChange={(e) => {
            if (e.target.value === 'completada') { startComplete(); return; }
            patch({ status: e.target.value });
          }}
          disabled={disabled}
          data-testid={`task-status-${task.id}`}>
          <option value="pendiente">Pendiente</option>
          <option value="en_curso">En curso</option>
          <option value="completada" disabled={!task.assignee_id}>
            {task.assignee_id ? 'Completada' : 'Completada (asigne resp.)'}
          </option>
          {!task.legal_obligatoria && <option value="no_aplica">No aplica</option>}
        </select>
      </div>

      {/* Formulario de evidencia al completar */}
      {confirming && (
        <div className="ml-[27px] mt-2 bg-[#F0F7F5] border border-[#143A34] p-3" data-testid={`task-confirm-${task.id}`}>
          <div className="text-[11.5px] font-medium text-[#0F2A26] mb-1 flex items-center gap-1.5">
            <ShieldCheck size={12} /> Deje evidencia de qué se hizo
          </div>
          <div className="text-[10.5px] text-[#374151] mb-2">
            {task.legal_obligatoria
              ? 'Obligatorio: describa brevemente cómo se cumplió esta tarea. Queda registrado en la bitácora ante una fiscalización de la Agencia.'
              : 'Opcional pero recomendado: qué se hizo, dónde queda la evidencia (link a documento, ubicación).'}
          </div>
          <textarea rows={3} className="w-full text-[12px] p-2 border border-[#D1D5DB] bg-white"
            placeholder={task.legal_obligatoria
              ? 'Ej: Se convocó al comité de crisis por Slack a las 14:35, participaron X, Y, Z. Acta guardada en /crisis/2026-02-15.'
              : 'Ej: Se ejecutó por Fulano el 15/02 · evidencia en Drive/…'}
            value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)}
            data-testid={`task-notes-${task.id}`} />
          <div className="flex items-center justify-between mt-2 gap-2">
            <div className="text-[10px] text-[#6B7280]">
              {task.legal_obligatoria && `Mínimo 10 caracteres (${notesDraft.trim().length})`}
            </div>
            <div className="flex gap-1.5">
              <button className="px-2.5 py-1 border border-[#D1D5DB] bg-white text-[11.5px] text-[#374151]"
                onClick={() => setConfirming(false)}>Cancelar</button>
              <button
                className="px-2.5 py-1 bg-[#143A34] text-white text-[11.5px] font-medium disabled:opacity-40 flex items-center gap-1"
                disabled={task.legal_obligatoria && notesDraft.trim().length < 10}
                onClick={confirmComplete}
                data-testid={`task-confirm-btn-${task.id}`}>
                <CheckCircle2 size={11} /> Marcar completada
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LÍNEA 3 · quién cerró + evidencia + botón eliminar */}
      {(isDone || canDelete) && (
        <div className="ml-[27px] mt-1 flex items-center gap-3 text-[10px] text-[#6B7280] flex-wrap">
          {isDone && task.completed_by_email && (
            <span className="text-[#065F46] inline-flex items-center gap-1">
              <CheckCircle2 size={9} /> Cerrada por {task.completed_by_email} · {formatDateTime(task.completed_at)}
              {task.assignee_email && task.completed_by_email !== task.assignee_email && (
                <span className="text-[#6B7280]">(resp.: {task.assignee_email})</span>
              )}
            </span>
          )}
          {isDone && task.completion_notes && (
            <button onClick={() => setNotesOpen((v) => !v)}
              className="text-[#374151] hover:text-[#143A34] inline-flex items-center gap-1"
              data-testid={`task-view-notes-${task.id}`}>
              <FileText size={9} /> {notesOpen ? 'Ocultar evidencia' : 'Ver evidencia'}
            </button>
          )}
          {canDelete && !readOnly && (
            <button onClick={remove}
              className="ml-auto text-[#B91C1C] hover:text-[#7F1D1D] inline-flex items-center gap-1"
              data-testid={`task-delete-${task.id}`}>
              <Trash2 size={10} /> Eliminar
            </button>
          )}
        </div>
      )}

      {/* Evidencia registrada al completar */}
      {isDone && notesOpen && task.completion_notes && (
        <div className="ml-[27px] mt-1.5 text-[11.5px] text-[#374151] bg-white border-l-2 border-[#10B981] pl-2.5 py-1.5">
          <div className="text-[10px] uppercase tracking-wider text-[#065F46] font-medium mb-0.5">Evidencia de cumplimiento</div>
          <div className="whitespace-pre-wrap">{task.completion_notes}</div>
        </div>
      )}
    </div>
  );
}


// ============ Formulario nueva tarea ============
function NewTaskForm({ stageKey, team, breachId, onSaved, onCancel }) {
  const [form, setForm] = useState({ title: '', description: '', assignee_id: '', due_date: '' });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const canSave = form.title.trim().length > 2;
  const save = async () => {
    try {
      await api.post(`/breaches/${breachId}/tasks`, {
        stage: stageKey, title: form.title.trim(),
        description: form.description.trim() || null,
        assignee_id: form.assignee_id || null,
        due_date: form.due_date ? form.due_date + ':00Z' : null,
      });
      toast.success('Tarea creada'); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };
  return (
    <div className="border border-[#143A34] bg-[#F0F7F5] p-4 space-y-3" data-testid="new-task-form">
      <div className="text-[12px] font-medium text-[#0F2A26]">Nueva tarea propia</div>
      <input className="pv-input" placeholder="Título (ej: Preparar comunicado interno)"
        value={form.title} onChange={set('title')} data-testid="new-task-title" />
      <textarea className="pv-input" rows={2} placeholder="Descripción (opcional): qué hay que hacer y por qué."
        value={form.description} onChange={set('description')} />
      <div className="grid grid-cols-2 gap-3">
        <select className="pv-input text-[12px]" value={form.assignee_id} onChange={set('assignee_id')}>
          <option value="">Sin asignar</option>
          {team.map((u) => <option key={u.id} value={u.id}>{u.nombre} {u.apellido}</option>)}
        </select>
        <input type="datetime-local" className="pv-input text-[12px]" value={form.due_date} onChange={set('due_date')} />
      </div>
      <div className="flex justify-end gap-2">
        <button className="pv-btn-secondary text-[12px]" onClick={onCancel}>Cancelar</button>
        <button className="px-3 py-1.5 bg-[#143A34] text-white text-[12px] font-medium disabled:opacity-50"
          disabled={!canSave} onClick={save} data-testid="new-task-save">Crear tarea</button>
      </div>
    </div>
  );
}


// ============ Plantillas de notificación ============
function TemplateBlock({ title, text, onChange, sent, onMarkSent, testid }) {
  return (
    <div className="border border-[#D1D5DB] bg-[#FAFBFC]" data-testid={testid}>
      <div className="px-3 py-2 border-b border-[#D1D5DB] flex items-center justify-between">
        <div className="text-[12px] font-medium text-[#1C2329]">{title}</div>
        <div className="flex items-center gap-2">
          <button className="text-[11px] text-[#374151] hover:text-[#143A34] flex items-center gap-1"
            onClick={() => { navigator.clipboard.writeText(text); toast.success('Copiado'); }}>
            <Copy size={11} /> Copiar
          </button>
          {sent ? (
            <span className="text-[11px] text-[#065F46] inline-flex items-center gap-1"><CheckCircle2 size={11} /> Enviado {formatDateTime(sent)}</span>
          ) : (
            <button className="text-[11px] px-2 py-1 bg-[#143A34] text-white hover:bg-[#0F2A26] flex items-center gap-1" onClick={onMarkSent}>
              <Send size={11} /> Marcar como enviado
            </button>
          )}
        </div>
      </div>
      <textarea className="w-full p-3 text-[12.5px] font-mono border-0 focus:outline-none bg-transparent min-h-[180px] whitespace-pre-wrap"
        value={text} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
